import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

export type StepType = 
  | 'new_lead'
  | 'appointment_set'
  | 'appointment_completed'
  | 'enrolled'
  | 'started'
  | 'connected';

  // This is a VALUE (exists at runtime)
export const STEP_TYPES = {
  NEW_LEAD: 'new_lead',
  APPOINTMENT_SET: 'appointment_set',
  APPOINTMENT_COMPLETED: 'appointment_completed',
  ENROLLED: 'enrolled',
  STARTED: 'started',
  CONNECTED: 'connected',
} 

export const STEP_TYPE_OPTIONS: { value: StepType | null; label: string }[] = [
  { value: null, label: 'None' },
  { value: 'new_lead', label: 'New Lead' },
  { value: 'appointment_set', label: 'Appointment Set' },
  { value: 'appointment_completed', label: 'Appointment Completed' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'started', label: 'Started' },
  { value: 'connected', label: 'Connected' }
];

export interface StepData {
  id?: number;
  name: string;
  rotting_in_days: number | null;
  probability: number;
  active: boolean;
  sequence: number;
  enable_rotting_days: boolean;
  step_type: StepType | null;  // Changed from stepType
}

@Component({
  selector: 'app-step-add-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule
  ],
  templateUrl: './step-add-edit.component.html',
  styleUrls: ['./step-add-edit.component.scss']
})
export class StepAddEditComponent implements OnInit {
  stepName: string = '';
  probability: number | '' = 100;
  rotting_in_days: number = 0;
  enableRotting: boolean = false;
  editMode: boolean = false;
  steps: StepData[] = [];
  currentRecord: StepData | null = null;
  step_type: StepType | null = null;  // Changed from stepType
  availableStepTypeOptions: { value: StepType | null; label: string }[] = [];

  constructor(
    public dialogRef: MatDialogRef<StepAddEditComponent>,  
    @Optional() @Inject(MAT_DIALOG_DATA) public data: {
      existingSteps: StepData[];
      editMode: boolean;
      currentRecord?: StepData;
    }
  ) {}

  ngOnInit(): void {
    if (!this.data) return;
  
    this.steps = this.data.existingSteps || [];
    this.editMode = this.data.editMode || false;
    this.currentRecord = this.data.currentRecord || null;
    
    // Filter available step types
    this.filterAvailableStepTypes();
    
    if (this.editMode && this.currentRecord) {
      this.stepName = this.currentRecord.name;
      this.probability = this.currentRecord.probability;
      this.step_type = this.currentRecord.step_type || null;  // Changed
      
      if (this.currentRecord.enable_rotting_days !== undefined) {
        this.enableRotting = this.currentRecord.enable_rotting_days;
      } else {
        this.enableRotting = this.currentRecord.rotting_in_days !== null;
      }
      
      if (this.currentRecord.rotting_in_days !== null) {
        this.rotting_in_days = this.currentRecord.rotting_in_days;
      }
    }
  }


  /**
   * Filter step types that are already used by other steps
   * Allow the current step's type when editing
   */
  filterAvailableStepTypes(): void {
    // Get all step types already used by other steps
    const usedStepTypes = this.steps
      .filter(step => {
        // Exclude current step when editing
        if (this.editMode && this.currentRecord) {
          return step.id !== this.currentRecord.id;  // Use id instead of sequence
        }
        return true;
      })
      .map(step => step.step_type)  // Changed from stepType
      .filter(type => type !== null);

    // Filter out used step types from options
    this.availableStepTypeOptions = STEP_TYPE_OPTIONS.filter(option => {
      // Always include 'None' option
      if (option.value === null) return true;
      // Include if not used by another step
      return !usedStepTypes.includes(option.value);
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.stepName) return;
    if (this.isProbabilityInvalid()) return;
    
    let sequence = 0;
    if (this.editMode && this.currentRecord) {
      sequence = this.currentRecord.sequence;
    } else if (this.steps.length > 0) {
      sequence = Math.max(...this.steps.map(step => step.sequence)) + 1;
    }
    
    const stepData: StepData = {
      enable_rotting_days: this.enableRotting,
      rotting_in_days: this.enableRotting ? this.rotting_in_days : null,
      name: this.stepName,
      probability: Number(this.probability),
      active: true,
      sequence: sequence,
      step_type: this.step_type  // Changed from stepType
    };
    
    this.dialogRef.close(stepData);
  }
  
  isProbabilityInvalid(): boolean {
    return (
      this.probability === '' ||
      this.probability === null ||
      this.probability === undefined ||
      Number(this.probability) > 100
    );
  }
  
  getProbabilityErrorMessage(): string {
    if (this.probability === '' || this.probability === null || this.probability === undefined) {
      return 'Probability is required.';
    }
    if (Number(this.probability) > 100) {
      return 'Probability must be 100 or lower.';
    }
    return '';
  }
}